import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { BannerAction } from '#pplx/frontend/aether/components/Banner/BannerAction.js';
import { BannerProvider } from '#pplx/frontend/aether/components/Banner/BannerContext.js';
import { Button } from '#pplx/frontend/aether/components/Button/index.js';
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { Spinner } from '#pplx/frontend/aether/components/Spinner/index.js';
import { useAetherLabels } from '#pplx/frontend/aether/internal/labels/useAetherLabels.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { cva } from 'class-variance-authority';
import classNames from 'classnames';
import { isValidElement, useMemo } from 'react';
const getBannerClasses = cva(classNames(
// Padding
'pt-3', 
// Flex container
'relative flex flex-col items-center w-full', 
// Rounded corners
'rounded-xl', 
// Box shadow
'shadow-sm ', 
// Container query responsive
'@md/banner:flex-row @md/banner:flex-wrap @md/banner:py-3'), {
    variants: {
        size: {
            default: 'gap-4 p-4 @md/banner:gap-4',
            compact: 'gap-2 p-2 @md/banner:gap-2',
        },
        variant: {
            default: 'bg-subtle',
            raised: 'border border-transparent forced-colors:border-[CanvasText] bg-raised',
            info: 'bg-super-soft',
            success: 'bg-positive-soft',
            warning: 'bg-warning-soft',
            error: 'bg-negative-soft',
        },
    },
    defaultVariants: {
        size: 'default',
        variant: 'default',
    },
});
const getBannerTitleClasses = cva('font-semibold', {
    variants: {
        size: {
            default: 'text-sm',
            compact: 'text-xs',
        },
        variant: {
            default: '',
            raised: '',
            info: 'text-primary',
            success: 'text-positive-primary',
            warning: 'text-warning-primary',
            error: 'text-negative-primary',
        },
    },
    defaultVariants: {
        size: 'default',
        variant: 'default',
    },
});
const getBannerDescriptionClasses = cva('mb-0 font-normal leading-[1.3]', {
    variants: {
        size: {
            default: 'text-sm',
            compact: 'text-xs',
        },
        variant: {
            default: 'text-secondary',
            raised: 'text-secondary',
            info: 'text-secondary',
            success: 'text-positive-secondary',
            warning: 'text-warning-secondary',
            error: 'text-negative-secondary',
        },
    },
    defaultVariants: {
        size: 'default',
        variant: 'default',
    },
});
const BANNER_CONTENT_CLASSES = classNames(
// Layout
'relative flex w-full flex-row items-center', 
// Spacing
'gap-3', 
// From @xl up, the 60% basis lets the actions move inline on a single row and only
// wrap when content is long (>60% + actions width). Below @xl the content spans its
// own row: the close button ends that row in the top-right corner and full-width
// actions follow on the next row.
'@xl/banner:mr-2 @xl/banner:flex-[1_1_60%]');
const BANNER_ACTION_CLASSES = classNames(
// Layout
'flex w-full flex-col items-stretch', 
// Spacing
'gap-2', 
// Container query responsive - horizontal layout with wrapping for long content
'@xl/banner:flex-row-reverse @xl/banner:flex-wrap @xl/banner:justify-end @xl/banner:gap-2 @xl/banner:w-auto @xl/banner:items-center', 
// Push actions to the right when wrapped to a new line
'@xl/banner:ml-auto');
const getBannerFootnoteDividerClasses = cva('w-full border-t', {
    variants: {
        size: {
            default: '',
            compact: '',
        },
        variant: {
            default: 'border-base',
            raised: 'border-base',
            info: 'border-super-subtle',
            success: 'border-positive-subtle',
            warning: 'border-warning-subtle',
            error: 'border-negative-subtle',
        },
    },
    defaultVariants: {
        size: 'default',
        variant: 'default',
    },
});
const getBannerFootnoteClasses = cva('font-normal leading-[1.3]', {
    variants: {
        size: {
            default: 'text-xs',
            compact: 'text-[11px]',
        },
        variant: {
            default: 'text-secondary',
            raised: 'text-secondary',
            info: 'text-secondary',
            success: 'text-positive-secondary',
            warning: 'text-warning-secondary',
            error: 'text-negative-secondary',
        },
    },
    defaultVariants: {
        size: 'default',
        variant: 'default',
    },
});
const ICON_COLOR_CLASSES = {
    default: '',
    raised: '',
    info: 'text-super-primary',
    success: 'text-positive-primary',
    warning: 'text-warning-primary',
    error: 'text-negative-primary',
};
function BannerRoot({ title, description, footnote, leadingAccessory, loading, children, size = 'default', variant = 'default', ...closeProps }) {
    const { close: closeButtonLabel } = useAetherLabels();
    const onClose = 'onClose' in closeProps ? closeProps.onClose : undefined;
    const contextValue = useMemo(() => ({ size, variant }), [size, variant]);
    // Reverse children array so DOM order matches visual order in narrow containers
    // In wide containers, flex-row-reverse will display them in the correct order
    const reversedChildren = useMemo(() => {
        if (!children) {
            return [];
        }
        const childArray = Array.isArray(children) ? children : [children];
        return [...childArray].reverse();
    }, [children]);
    // Match Banner.Action size: 'small' for default banner size, 'tiny' for compact
    const closeButtonSize = size === 'default' ? 'small' : 'tiny';
    const closeButton = onClose ? (_jsx(Button, { icon: $icon('x'), variant: "text", size: closeButtonSize, onClick: onClose, "aria-label": closeButtonLabel })) : null;
    // Determine if actions container should render
    const hasActions = reversedChildren.length > 0;
    return (_jsx(BannerProvider, { value: contextValue, children: _jsx("div", { className: "@container/banner", role: loading !== undefined ? 'status' : undefined, "aria-busy": loading !== undefined ? loading : undefined, children: _jsxs("div", { className: getBannerClasses({ size, variant }), children: [_jsxs("div", { className: BANNER_CONTENT_CLASSES, children: [(leadingAccessory || loading) && (_jsx("div", { className: classNames('shrink-0 flex items-center', ICON_COLOR_CLASSES[variant]), children: loading ? (_jsx(Spinner, { size: size === 'compact' ? 16 : 20 })) : isValidElement(leadingAccessory) ? (leadingAccessory) : (_jsx(Icon, { icon: leadingAccessory, size: size === 'compact' ? 'small' : 'default' })) })), _jsxs("div", { className: "flex-1 overflow-hidden", children: [closeButton && (_jsx("div", { className: "float-right ml-2 @md/banner:hidden", children: closeButton })), _jsx("div", { className: getBannerTitleClasses({ size, variant }), children: title }), description && (_jsx("div", { className: getBannerDescriptionClasses({ size, variant }), children: description })), _jsx("div", { className: "clear-both" })] }), closeButton && (_jsx("div", { className: classNames('hidden shrink-0 self-start @md/banner:block', hasActions && '@xl/banner:hidden'), children: closeButton }))] }), hasActions && (_jsxs("div", { className: BANNER_ACTION_CLASSES, children: [closeButton && _jsx("div", { className: "hidden shrink-0 @xl/banner:block", children: closeButton }), reversedChildren] })), footnote && (_jsxs("div", { className: classNames('flex w-full flex-col', size === 'compact' ? 'gap-2' : 'gap-4'), children: [_jsx("div", { className: getBannerFootnoteDividerClasses({ size, variant }) }), _jsx("div", { className: classNames('w-full', getBannerFootnoteClasses({ size, variant })), children: footnote })] }))] }) }) }));
}
/**
 * Full-width notification banners for displaying system messages, alerts and status updates.
 * Supports an optional close button and Banner.Action children for CTA buttons or links.
 *
 * @category components
 * @keywords banner, components, full-width, notification, banners, displaying, system, messages,
 *   alerts, status, updates, supports, optional, close, button, banneraction, children, buttons,
 *   links, loading, icon, spinner
 * @whenToUse Use Banner to display prominent, contextual messages that inform users about system
 *   status, feature announcements, warnings, or errors. Banners are ideal for page-level or
 *   section-level notifications that require user attention and optionally provide actions such as
 *   dismissing the message, navigating to a related page, or triggering a workflow. Choose the
 *   appropriate variant to match the severity or intent of the message.
 * @guidelineDo
 *   - Use the appropriate semantic variant (info, success, warning, error) to match the message
 *     intent
 *   - Provide onClose for informational banners that users can dismiss
 *   - Use Banner.Action sub-components for consistent action styling within the banner
 *   - Keep the title concise and use the description prop for supplementary details
 *   - Use the loading prop to indicate asynchronous operations in progress
 * @guidelineDont
 *   - Don't nest interactive elements directly as children — use Banner.Action for buttons and links
 *   - Don't use the default variant when a semantic variant (info, warning, error, success) better
 *     communicates the message type
 *   - Don't omit the leadingAccessory icon when using semantic variants, as it reinforces the visual
 *     intent
 *   - Don't make critical errors dismissible before the user can resolve them
 *   - Don't use compact size for banners with long descriptions or multiple actions
 * @accessibility
 *   - When the loading prop is defined (true or false), the banner renders with role="status" and
 *     aria-busy, making it a live region for screen readers
 *   - The close button uses the AetherProvider close label for its accessible name
 *   - Banner uses a responsive container query layout — the close button repositions between mobile
 *     and desktop views while remaining keyboard-accessible in both
 *   - Action items support both onClick (button behavior) and href (link behavior) with proper
 *     semantic elements
 *   - Use semantic variants (info, success, warning, error) to provide color-based context that
 *     pairs with the leadingAccessory icon for non-color-reliant communication
 * @relatedComponents
 *   - button
 *   - icon
 *   - spinner
 */
export const Banner = Object.assign(BannerRoot, {
    Action: BannerAction,
});
//# sourceMappingURL=index.js.map